// user-dashboard/components/AdvicePasswordsTable.jsx
import React, { useRef, useState, useEffect, useMemo } from "react";
import Secret from "./Secret";
import MessageCarousel from "./MessageCarousel";
import SearchableSelect from "./SearchableSelect";
import StatusBubble from "../../../components/StatusBubble";
import ProxyDisplay, { MlProfileStatus } from "../../passwords/components/ProxyDisplay";
import { copyToClipboard } from "../../passwords/utils/copyToClipboard";
import PersonaCheckModal from "./PersonaCheckModal";
import SelfTransferConfirmModal from "./SelfTransferConfirmModal";
import ConvertToTtOtpModal from "./ConvertToTtOtpModal";
import GetNewTmAccountModal from "./GetNewTmAccountModal";
import { getSessionStampWho, getSessionStampedAs, fetchMyTmAccountRequests, fetchTmAccountRequests } from "../../../api";
import { isSimConvertedYes, isServiceNumber, canRequestSimConversion, isSimConversionRequested, resolvePortForConversion, shouldShowActivate, portRecordForActivate } from "../../../shared/portActivation";
import { tmRequestRowKey, completedTmByOriginalKey as completedTmByOriginalKeyFromRows, completedTmByNewKey as completedTmByNewKeyFromRows } from "../../../shared/tmAccountRequest";

const RED_CELL = { backgroundColor: "#dc3545", color: "#fff" };
const GREEN_CELL = { backgroundColor: "#198754", color: "#fff" };
const TM_REQUEST_ROW_STYLE = {
  backgroundColor: "#cfe2ff",
  "--bs-table-bg": "#cfe2ff",
  "--bs-table-accent-bg": "#cfe2ff",
  "--bs-table-striped-bg": "#cfe2ff",
  borderLeft: "6px solid #0d6efd",
};

function emptyPendingTmRequests() {
  return { keys: new Set(), byKey: new Map() };
}

function rowHasPendingTmRequest(record, pendingTmRequests) {
  const key = tmRequestRowKey(record);
  return !!(key && pendingTmRequests?.keys?.has(key));
}

function portHasAssignedNumber(port) {
  return !!(port && String(port.assigned_number || "").trim());
}

function isSimConverted(port) {
  return isSimConvertedYes(port);
}

function canRequestConvertToTtOtp(port) {
  return canRequestSimConversion(port);
}

function conversionRequestedTitle(port) {
  if (!isSimConversionRequested(port)) return "Conversion requested";
  const who = (port.req_sim_conversion_by || "").trim();
  const when = port.req_sim_conversion_at
    ? new Date(port.req_sim_conversion_at).toLocaleString()
    : "";
  const requestId = port.req_sim_conversion_id;
  const source = (port.req_sim_conversion_source || "").trim().toLowerCase();
  const parts = [];
  if (requestId) {
    parts.push(source === "bulk" ? `Bulk request #${requestId}` : `Request #${requestId}`);
  } else if (source === "bulk") {
    parts.push("Bulk request");
  }
  if (who && when) parts.push(`Requested ${when} by ${who}`);
  else if (when) parts.push(`Requested ${when}`);
  else if (who) parts.push(`Requested by ${who}`);
  return parts.join(" — ") || "Conversion requested";
}

/**
 * AdvicePasswordsTable
 *
 * Shows advice email/passwords joined with linked ports.
 * Features: column search, resizable columns, hidden Main column, always-visible passwords
 */
export default function AdvicePasswordsTable({
  filteredAdvice = [], // ✅ defensive default
  groupedByUserCode = null, // ✅ NEW: grouped data for display
  portsByEmail = new Map(), // ✅ defensive default
  editingId = null, // ✅ defensive default
  editDraft = { site: "", email: "", password: "" }, // ✅ defensive default
  startEdit,
  cancelEdit,
  saveEdit,
  setEditDraft,
  toggleSort,
  sortField,
  sortDir,
  onActivatePort,
  onReportProblem,
  getButtonState,
  getStatusBadge,
  setDetailsRecord,
  setShowDetails,
  portStatus = {}, // ✅ defensive default
  portMessages = {}, // ✅ defensive default
  portCountdowns = {}, // ✅ defensive default
  clearPortMessage,
  columnFilters = {
    userCode: "",
    site: "",
    email: "",
    password: "",
    numbers: "",
    personaCheck: "",
    selfTransfer: "",
    conversionStatus: "",
  }, // ✅ defensive default
  setColumnFilters,
  onUpdatePort = null, // ✅ NEW: callback to update port phone number (for gateway ports)
  onClearPort = null, // clear port (bin SIM) so slot is free for next SIM
  onDeleteAdvicePassword = null, // delete email row from dashboard
  isStaff = false, // ✅ NEW: whether this is staff view
  sites = [], // ✅ NEW: list of available sites for dropdown
  currentUserCode = typeof localStorage !== "undefined" ? (localStorage.getItem("user_code") || "") : "",
  duplicateEmails = new Set(),
  duplicateNumbers = new Set(),
  multiloginOverrides = {},
  onToggleMultilogin = null,
  onRequestForwardingApproval = null,
  availablePorts = [],
  allPorts = [],
  onAssignPort = null,
  simplifyMatchingProxy = false,
  onPersonaCheckSubmit = null,
  onPersonaCheckClear = null,
  onSelfTransferToggle = null,
  onRequestSimConversion = null,
  pendingTmRequestKeys = null,
  tmRequestsByKey = null,
  completedTmByOriginalKey = null,
  completedTmByNewKey = null,
  onTmAccountRequested = null,
}) {
  // Helper — checks the row's email AND all its linked port numbers
  const isRowDuplicate = (row) => {
    const email = (row.email || "").trim().toLowerCase();
    if (email && duplicateEmails.has(email)) return true;

    const linkedPorts = portsByEmail.get(email) || [];
    return linkedPorts.some((port) => {
      const num = String(port.assigned_number || "").replace(/\D/g, "");
      return num && duplicateNumbers.has(num);
    });
  };

  // Column width state (px)
  const [columnWidths, setColumnWidths] = useState({
    userCode: 120,
    site: 160,
    email: 220,
    password: 200,
    proxy: 220,
    personaCheck: 110,
    selfTransfer: 110,
    numbers: 200,
    status: 120,
    actions: 200,
    messages: 320,
    details: 120,
    edit: 160,
  });

  // Build a phone-number → port[] lookup so rows with user_managed_numbers
  // (no email-linked ports) can still show status, messages, activate, and report.
  const portsByNumber = useMemo(() => {
    const map = new Map();
    const addPort = (port) => {
      if (!port || port.archived) return;
      const num = String(port.assigned_number || "").replace(/\D/g, "");
      if (!num) return;
      if (!map.has(num)) map.set(num, []);
      if (!map.get(num).some((p) => p.id === port.id)) map.get(num).push(port);
    };
    for (const port of allPorts || []) addPort(port);
    for (const ports of (portsByEmail || new Map()).values()) {
      for (const port of ports) addPort(port);
    }
    return map;
  }, [portsByEmail, allPorts]);

  const tableRef = useRef(null);
  const [resizing, setResizing] = useState(null);
  // State for editing port phone numbers (keyed by port ID)
  const [editingPortNumbers, setEditingPortNumbers] = useState(new Map());
  const [assigningPortForRowId, setAssigningPortForRowId] = useState(null);
  const [assigningPortId, setAssigningPortId] = useState("");
  const [assigningError, setAssigningError] = useState(null);
  const [personaModal, setPersonaModal] = useState({ open: false, mode: "edit", record: null });
  const [selfTransferModal, setSelfTransferModal] = useState({ open: false, record: null });
  const [conversionModal, setConversionModal] = useState({ open: false, port: null, record: null });
  const [tmAccountModal, setTmAccountModal] = useState({ open: false, record: null });
  const [pendingTmRequests, setPendingTmRequests] = useState(emptyPendingTmRequests);
  const [completedByOriginal, setCompletedByOriginal] = useState(() => new Map());
  const [completedByNew, setCompletedByNew] = useState(() => new Map());
  const [flashAdviceId, setFlashAdviceId] = useState(null);
  const [expandedAltEmailKeys, setExpandedAltEmailKeys] = useState(() => new Set());
  const [savingFlags, setSavingFlags] = useState({});

  useEffect(() => {
    if (completedTmByOriginalKey instanceof Map) {
      setCompletedByOriginal(completedTmByOriginalKey);
    }
    if (completedTmByNewKey instanceof Map) {
      setCompletedByNew(completedTmByNewKey);
    }
  }, [completedTmByOriginalKey, completedTmByNewKey]);

  useEffect(() => {
    if (pendingTmRequestKeys instanceof Set) {
      setPendingTmRequests((prev) => ({
        keys: new Set(pendingTmRequestKeys),
        byKey: prev.byKey || new Map(),
      }));
      return undefined;
    }
    let cancelled = false;
    const load = isStaff
      ? Promise.all([
          fetchTmAccountRequests({ status: "pending" }),
          fetchTmAccountRequests({ status: "completed" }),
        ])
      : Promise.all([
          fetchMyTmAccountRequests({ status: "pending" }),
          fetchMyTmAccountRequests({ status: "completed" }),
        ]);
    load
      .then(([pendingRows, completedRows]) => {
        if (cancelled) return;
        setPendingTmRequests((prev) => {
          const keys = new Set(prev.keys);
          const byKey = new Map(prev.byKey);
          for (const req of pendingRows || []) {
            const key = tmRequestRowKey(req);
            if (!key) continue;
            keys.add(key);
            byKey.set(key, req);
          }
          return { keys, byKey };
        });
        setCompletedByOriginal(completedTmByOriginalKeyFromRows(completedRows));
        setCompletedByNew(completedTmByNewKeyFromRows(completedRows));
      })
      .catch(() => {
        // Keep any optimistic pending rows if the list endpoint fails.
      });
    return () => {
      cancelled = true;
    };
  }, [isStaff, pendingTmRequestKeys]);

  useEffect(() => {
    if (!editingId) {
      setAssigningPortForRowId(null);
      setAssigningPortId("");
      setAssigningError(null);
    }
  }, [editingId]);


  // Handle column resize
  useEffect(() => {
    if (!resizing) return;

    const handleMouseMove = (e) => {
      const delta = e.clientX - resizing.startX;
      const newWidth = Math.max(60, (resizing.startWidth || 0) + delta);

      setColumnWidths((prev) => ({
        ...prev,
        [resizing.column]: newWidth,
      }));
    };

    const handleMouseUp = () => {
      setResizing(null);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [resizing]);

  const handleResizeStart = (column, e) => {
    e.preventDefault();
    setResizing({
      column,
      startX: e.clientX,
      startWidth: columnWidths[column] || 120,
    });
  };

  const showPersonaSelfTransfer = !!(onPersonaCheckSubmit || onSelfTransferToggle);
  const colSpan = (groupedByUserCode ? 11 : 10) + (showPersonaSelfTransfer ? 2 : 0);

  const handlePersonaCheckboxChange = (r, checked) => {
    if (checked) {
      if (r.persona_check_triggered) return;
      setPersonaModal({ open: true, mode: "edit", record: r });
      return;
    }
    if (!onPersonaCheckClear) return;
    if (!window.confirm("Clear persona check for this email?")) return;
    setSavingFlags((prev) => ({ ...prev, [`persona_${r.id}`]: true }));
    Promise.resolve(onPersonaCheckClear(r))
      .catch((err) => console.error("Failed to clear persona check:", err))
      .finally(() => {
        setSavingFlags((prev) => {
          const next = { ...prev };
          delete next[`persona_${r.id}`];
          return next;
        });
      });
  };

  const handlePersonaModalSubmit = async ({ stampedAs, stampedBy, answers }) => {
    const record = personaModal.record;
    if (!record || !onPersonaCheckSubmit) return;
    await onPersonaCheckSubmit(record, { stampedAs, stampedBy, answers });
  };

  const handleSelfTransferChange = (r, checked) => {
    if (!onSelfTransferToggle) return;
    if (checked) {
      if (r.self_manage_transfer) return;
      setSelfTransferModal({ open: true, record: r });
      return;
    }
    if (!window.confirm("Disable Self TM Transfer for this email?")) return;
    runSelfTransferToggle(r, false);
  };

  const runSelfTransferToggle = (r, checked) => {
    setSavingFlags((prev) => ({ ...prev, [`self_${r.id}`]: true }));
    return Promise.resolve(onSelfTransferToggle(r, checked))
      .catch((err) => {
        console.error("Failed to update self transfer:", err);
        throw err;
      })
      .finally(() => {
        setSavingFlags((prev) => {
          const next = { ...prev };
          delete next[`self_${r.id}`];
          return next;
        });
      });
  };

  const handleSelfTransferConfirm = async () => {
    const record = selfTransferModal.record;
    if (!record) return;
    await runSelfTransferToggle(record, true);
  };

  const renderConversionRequestedIndicator = (port) => {
    const resolved = resolvePortForConversion(port, allPorts);
    if (!resolved) return null;

    // Converted (is_sim_converted = 2) — may have no note/date/request fields
    if (isSimConverted(resolved)) {
      const when = resolved.sim_converted_on
        ? new Date(resolved.sim_converted_on).toLocaleString()
        : "";
      const note = (resolved.sim_converted_note || "").trim();
      const titleParts = ["SIM converted to TT OTP"];
      if (when) titleParts.push(when);
      if (note) titleParts.push(note);
      return (
        <span
          className="badge"
          title={titleParts.join(" — ")}
          style={{
            backgroundColor: "#6c757d",
            color: "#fff",
            fontSize: "0.65rem",
            fontWeight: 600,
            alignSelf: "flex-start",
          }}
        >
          OTP SIM
        </span>
      );
    }

    if (isServiceNumber(resolved)) {
      return (
        <span
          className="badge"
          title="Service number"
          style={{
            backgroundColor: "#0dcaf0",
            color: "#000",
            fontSize: "0.65rem",
            fontWeight: 600,
            alignSelf: "flex-start",
          }}
        >
          Service
        </span>
      );
    }

    if (!isSimConversionRequested(resolved)) return null;
    return (
      <span
        className="badge"
        title={conversionRequestedTitle(resolved)}
        style={{
          backgroundColor: "#fd7e14",
          color: "#fff",
          fontSize: "0.65rem",
          fontWeight: 600,
          alignSelf: "flex-start",
        }}
      >
        Conversion requested
        {resolved.req_sim_conversion_id ? ` #${resolved.req_sim_conversion_id}` : ""}
        {resolved.req_sim_conversion_source === "bulk" ? " (bulk)" : ""}
      </span>
    );
  };

  const handleConversionConfirm = async () => {
    const port = conversionModal.port;
    if (!port || !onRequestSimConversion) return;
    await onRequestSimConversion(port);
  };

  const renderPersonaCheckCell = (r) => {
    const checked = !!r.persona_check_triggered;
    const saving = !!savingFlags[`persona_${r.id}`];
    const interactive = !!(onPersonaCheckSubmit || onPersonaCheckClear);
    return (
      <td className="text-center align-middle" style={checked ? RED_CELL : undefined}>
        <div className="d-flex flex-column align-items-center gap-1">
          <input
            type="checkbox"
            className="form-check-input m-0"
            checked={checked}
            disabled={saving || (!interactive && !checked)}
            onChange={(e) => handlePersonaCheckboxChange(r, e.target.checked)}
            title={checked ? "Persona check complete" : "Mark persona check"}
            style={{ cursor: interactive ? "pointer" : "default" }}
          />
          {checked && (
            <button
              type="button"
              className="btn btn-link btn-sm p-0"
              style={{ fontSize: "0.7rem", color: checked ? "#fff" : undefined, textDecoration: "underline" }}
              onClick={() => setPersonaModal({ open: true, mode: "view", record: r })}
            >
              answers
            </button>
          )}
          {saving && (
            <span className="spinner-border spinner-border-sm" style={{ width: 10, height: 10 }} />
          )}
        </div>
      </td>
    );
  };

  const renderSelfTransferCell = (r) => {
    const checked = !!r.self_manage_transfer;
    const saving = !!savingFlags[`self_${r.id}`];
    const interactive = !!onSelfTransferToggle;
    return (
      <td className="text-center align-middle" style={checked ? GREEN_CELL : undefined}>
        <div className="d-flex flex-column align-items-center gap-1">
          <input
            type="checkbox"
            className="form-check-input m-0"
            checked={checked}
            disabled={saving || !interactive}
            onChange={(e) => handleSelfTransferChange(r, e.target.checked)}
            title={
              checked && r.self_manage_transfer_since
                ? `Since ${new Date(r.self_manage_transfer_since).toLocaleString()}`
                : "Self transfer"
            }
            style={{ cursor: interactive ? "pointer" : "default" }}
          />
          {checked && r.self_manage_transfer_since && (
            <span style={{ fontSize: "0.65rem", lineHeight: 1.1, opacity: 0.9 }}>
              {new Date(r.self_manage_transfer_since).toLocaleDateString()}
            </span>
          )}
          {saving && (
            <span className="spinner-border spinner-border-sm" style={{ width: 10, height: 10 }} />
          )}
        </div>
      </td>
    );
  };

  const renderEditCell = (r, linkedPorts, isEditing) => (
    <div className="d-flex gap-1 align-items-center flex-wrap">
      {onToggleMultilogin && (
        <div className="d-flex flex-column gap-1 mb-1">
          <div className="form-check form-switch mb-0">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id={`ml-switch-${r.id}`}
              checked={multiloginOverrides[r.id] ?? r.multilogin ?? false}
              disabled={!!multiloginOverrides[`${r.id}_saving`]}
              onChange={(e) => onToggleMultilogin(r, e.target.checked)}
              style={{ cursor: "pointer" }}
            />
            <label
              className="form-check-label small"
              htmlFor={`ml-switch-${r.id}`}
              style={{ cursor: "pointer" }}
            >
              {multiloginOverrides[`${r.id}_saving`] ? (
                <span className="spinner-border spinner-border-sm" style={{ width: 10, height: 10 }} />
              ) : (
                "ML"
              )}
            </label>
          </div>
          <MlProfileStatus
            multilogin={multiloginOverrides[r.id] ?? r.multilogin ?? false}
            mlProfile={r.ml_profile}
          />
        </div>
      )}
      {!isEditing ? (
        <>
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => startEdit && startEdit(r)}
          >
            Edit
          </button>
          {onDeleteAdvicePassword && (
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={async () => {
                const emailLabel = (r.email || "").trim() || "this email";
                const numbersToWipe = linkedPorts
                  .filter(p => (p.assigned_number || "").trim())
                  .map(p => p.assigned_number)
                  .join(", ");
                const numberWarning = numbersToWipe
                  ? `\n\nWARNING: This will permanently remove the following phone number(s) from the system:\n${numbersToWipe}`
                  : "";
                if (
                  !window.confirm(
                    `Remove "${emailLabel}" from your profile and free linked port slots?${numberWarning}\n\nYou can reassign those slots with different SIMs in Add new port.`
                  )
                )
                  return;
                try {
                  for (const port of linkedPorts) {
                    const canClear =
                      onClearPort &&
                      (port.gateway?.owner_user_code === currentUserCode ||
                        port.user_code === currentUserCode);
                    if (canClear) await onClearPort(port.id);
                  }
                  await onDeleteAdvicePassword(r.id);
                } catch (err) {
                  console.error("Error deleting line and freeing slots:", err);
                }
              }}
              title="Remove email from profile and free port slots for reassignment"
            >
              Delete email
            </button>
          )}
          {onRequestForwardingApproval && r.email && (
            <button
              className="btn btn-sm btn-outline-info"
              onClick={() => onRequestForwardingApproval(r.email)}
              title="Request forwarding approval for this email"
              style={{ whiteSpace: "nowrap" }}
            >
              📧 Fwd
            </button>
          )}
        </>
      ) : (
        <>
          <button
            className="btn btn-sm btn-success"
            onClick={() => saveEdit && saveEdit(r)}
          >
            Save
          </button>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={cancelEdit}
          >
            Cancel
          </button>
        </>
      )}
    </div>
  );

  const renderNumbersCell = (r, linkedPorts, safeId) => {
    const isEditing = editingId === r.id;

    if (linkedPorts.length === 0) {
      // Check for user_managed_numbers even when no ports are directly linked
      const managedNums = Array.isArray(r.user_managed_numbers) ? r.user_managed_numbers : [];

      if (managedNums.length > 0 && !isEditing) {
        return (
          <div className="d-flex flex-column gap-1">
            {managedNums.map((num, i) => {
              const digits = String(num || "").replace(/\D/g, "");
              const matchingPort = (portsByNumber.get(digits) || [])[0];
              return (
                <div key={i} className="d-flex flex-column gap-0">
                  <span className="small fw-medium" style={{ color: "#dc3545" }} title="Shared number (managed)">
                    {num}
                  </span>
                  {renderConversionRequestedIndicator(matchingPort)}
                </div>
              );
            })}
          </div>
        );
      }

      if (isEditing) {
        const unlinkedPorts = availablePorts.filter((p) => !p.archived);
        return (
          <div className="d-flex flex-column gap-1">
            {assigningError && assigningPortForRowId === r.id && (
              <div className="text-danger" style={{ fontSize: "11px" }}>{assigningError}</div>
            )}
            <select
              className="form-select form-select-sm"
              style={{ maxWidth: 200 }}
              value={assigningPortForRowId === r.id ? assigningPortId : ""}
              onChange={(e) => {
                setAssigningPortForRowId(r.id);
                setAssigningPortId(e.target.value);
              }}
            >
              <option value="">— Select a port —</option>
              {unlinkedPorts.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.port_number || "service"} {p.assigned_number ? `(${p.assigned_number})` : "(no number)"}
                </option>
              ))}
            </select>
            {assigningPortId && assigningPortForRowId === r.id && (
              <button
                className="btn btn-sm btn-success"
                style={{ maxWidth: 200 }}
                onClick={async () => {
                  setAssigningError(null);
                  try {
                    const selectedPort = availablePorts.find(
                      (p) => String(p.id) === String(assigningPortId)
                    );
                    const portNum = String(selectedPort?.assigned_number || "").replace(/\D/g, "");
                    if (portNum) {
                      const currentEmailKey = (r.email || "").trim().toLowerCase();
                      for (const [emailKey, emailPorts] of (portsByEmail || new Map()).entries()) {
                        if (emailKey === currentEmailKey) continue;
                        if (
                          emailPorts.some(
                            (p) =>
                              String(p.assigned_number || "").replace(/\D/g, "") === portNum
                          )
                        ) {
                          const confirmed = window.confirm(
                            `⚠️ ${selectedPort.assigned_number} is already linked to a different email.\n\nAssign it to ${r.email} as well?`
                          );
                          if (!confirmed) return;
                          break;
                        }
                      }
                    }
                    await onAssignPort?.(parseInt(assigningPortId, 10), r.email);
                    setAssigningPortForRowId(null);
                    setAssigningPortId("");
                  } catch (err) {
                    setAssigningError(err?.message || "Failed to assign");
                  }
                }}
              >
                Assign Port
              </button>
            )}
          </div>
        );
      }
      return <span className="text-muted small">No numbers</span>;
    }

    return (
      <div className="d-flex flex-column gap-1">
        {linkedPorts.map((port, portIdx) => {
          const isGatewayPort = port.gateway?.owner_user_code;
          const canEditNumber = isStaff || isGatewayPort || isServiceNumber(port);
          const isEditingNumber = editingPortNumbers.has(port.id);
          const editingValue = editingPortNumbers.get(port.id) || port?.assigned_number || "";
          return (
            <div
              key={port?.id ? `${safeId}-port-${port.id}` : `${safeId}-port-${portIdx}`}
              className="d-flex flex-column gap-0"
            >
              <div className="d-flex align-items-center gap-1">
              {isEditingNumber && canEditNumber ? (
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={editingValue}
                  onChange={(e) => {
                    const newMap = new Map(editingPortNumbers);
                    newMap.set(port.id, e.target.value);
                    setEditingPortNumbers(newMap);
                  }}
                  onBlur={async () => {
                    if (onUpdatePort && editingValue !== (port?.assigned_number || "")) {
                      // Check if this number is already linked to a different email
                      const newDigits = editingValue.replace(/\D/g, "");
                      if (newDigits) {
                        const currentEmailKey = (r.email || "").trim().toLowerCase();
                        let usedByOtherEmail = false;
                        for (const [emailKey, emailPorts] of (portsByEmail || new Map()).entries()) {
                          if (emailKey === currentEmailKey) continue;
                          if (emailPorts.some(p => String(p.assigned_number || "").replace(/\D/g, "") === newDigits)) {
                            usedByOtherEmail = true;
                            break;
                          }
                        }
                        if (usedByOtherEmail) {
                          const confirmed = window.confirm(
                            `⚠️ ${editingValue} is already linked to a different email.\n\nAre you sure you want to also assign it to ${r.email}?`
                          );
                          if (!confirmed) {
                            const newMap = new Map(editingPortNumbers);
                            newMap.delete(port.id);
                            setEditingPortNumbers(newMap);
                            return;
                          }
                        }
                      }
                      try {
                        await onUpdatePort(port.id, { assigned_number: editingValue });
                        const newMap = new Map(editingPortNumbers);
                        newMap.delete(port.id);
                        setEditingPortNumbers(newMap);
                      } catch (err) {
                        console.error("Error updating port number:", err);
                        alert(err.response?.data?.detail || err.message || "Failed to update phone number");
                      }
                    } else {
                      const newMap = new Map(editingPortNumbers);
                      newMap.delete(port.id);
                      setEditingPortNumbers(newMap);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") e.target.blur();
                    else if (e.key === "Escape") {
                      const newMap = new Map(editingPortNumbers);
                      newMap.delete(port.id);
                      setEditingPortNumbers(newMap);
                    }
                  }}
                  style={{ maxWidth: 150 }}
                  autoFocus
                />
              ) : (
                <>
                  {(() => {
                    const digits = (s) => String(s || "").replace(/\D/g, "");
                    const portDigits = digits(port?.assigned_number || "");
                    const isUserManaged =
                      r.user_managed_numbers &&
                      Array.isArray(r.user_managed_numbers) &&
                      r.user_managed_numbers.some((num) => digits(num) === portDigits);
                    return (
                      <span
                        className="small fw-medium"
                        style={{
                          color: port?.archived
                            ? '#dc3545'
                            : isUserManaged ? "#dc3545" : "inherit"
                        }}
                        title={port?.archived ? 'This number is no longer in use' : isUserManaged ? "User-managed number (not on system)" : ""}
                      >
                        {port?.archived ? 'No longer in use' : (port?.assigned_number || "—")}
                      </span>
                    );
                  })()}
                  {canEditNumber && (
                    <>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        style={{ padding: "0.125rem 0.25rem", fontSize: "0.75rem" }}
                        onClick={() => {
                          const newMap = new Map(editingPortNumbers);
                          newMap.set(port.id, port?.assigned_number || "");
                          setEditingPortNumbers(newMap);
                        }}
                        title="Edit phone number"
                      >
                        ✏️
                      </button>
                      {(port?.assigned_number || "").trim() && onClearPort && (
                        <button
                          className="btn btn-sm btn-outline-danger"
                          style={{ padding: "0.125rem 0.25rem", fontSize: "0.75rem" }}
                          onClick={() => {
                            const numLabel = (port?.assigned_number || "").trim();
                            const slotLabel = port?.port_number || port?.id;
                            const clearMsg = isServiceNumber(port)
                              ? `Remove service number ${numLabel || slotLabel}? This does not free a gateway slot.`
                              : numLabel
                              ? `Remove number ${numLabel} from port ${slotLabel}? This permanently removes the number from the system and frees the slot for a new SIM.`
                              : `Clear port ${slotLabel}? This frees the slot for the next SIM.`;
                            if (window.confirm(clearMsg)) {
                              onClearPort(port.id);
                            }
                          }}
                          title="Bin SIM / clear port (free slot for next SIM)"
                        >
                          🗑️
                        </button>
                      )}
                    </>
                  )}
                </>
              )}
              </div>
              {renderConversionRequestedIndicator(port)}
            </div>
          );
        })}
      </div>
    );
  };

  const renderPortStatus = (port, safeId, portIdx) => {
    const fullPort = resolvePortForConversion(port, allPorts);
    if (isSimConverted(fullPort)) {
      return (
        <StatusBubble
          status="active"
          showLabel
          compact={false}
        />
      );
    }
    const s = portStatus[port?.id] || {};
    const inWarmup =
      s.ui_warmup_until && Date.now() < s.ui_warmup_until && !s.message_triggered;
    const displayStatus =
      inWarmup && s.status === "active" ? "activating" : (s.status || "inactive");
    return (
      <StatusBubble
        status={displayStatus}
        signal={s.signal}
        operator={s.operator}
        animate={displayStatus === "activating"}
        showLabel={true}
        compact={false}
      />
    );
  };

  const markTmAccountRequested = (record, created = null) => {
    setPendingTmRequests((prev) => {
      const keys = new Set(prev.keys);
      const byKey = new Map(prev.byKey);
      const key = tmRequestRowKey(record);
      if (key) keys.add(key);
      if (key && created) byKey.set(key, created);
      return { keys, byKey };
    });
    onTmAccountRequested?.(record, created);
  };

  const tmRequestForRow = (record) => {
    const key = tmRequestRowKey(record);
    if (!key) return null;
    if (tmRequestsByKey?.has?.(key)) return tmRequestsByKey.get(key);
    if (!pendingTmRequests.keys?.has(key)) return null;
    return pendingTmRequests.byKey?.get(key) || null;
  };

  const scrollToAdviceRow = (adviceId) => {
    if (adviceId == null) return;
    const el = document.getElementById(`advice-row-${adviceId}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setFlashAdviceId(adviceId);
    window.setTimeout(() => {
      setFlashAdviceId((current) => (current === adviceId ? null : current));
    }, 1800);
  };

  const rowClassName = (record, hasPendingTmRequest) => {
    const classes = [];
    if (hasPendingTmRequest) classes.push("tm-account-requested");
    if (record?.id != null && flashAdviceId === record.id) classes.push("tm-alt-email-flash");
    return classes.join(" ") || undefined;
  };

  const renderEmailDisplay = (record) => {
    const key = tmRequestRowKey(record);
    // Replacement row → link back to order-restricted original.
    // Order-restricted row → link forward to replacement.
    const fromNew = key ? completedByNew.get(key) : null;
    const fromOriginal = !fromNew && key ? completedByOriginal.get(key) : null;
    const linkedReq = fromNew || fromOriginal;
    const isOriginalSide = !!fromOriginal;
    const altEmail = linkedReq
      ? (isOriginalSide ? linkedReq.new_email : linkedReq.email || "").trim()
      : "";
    const targetId = linkedReq
      ? isOriginalSide
        ? linkedReq.new_advice_password_id
        : linkedReq.advice_password_id
      : null;
    const expandKey = key || `id:${record?.id ?? ""}`;
    const isExpanded = expandedAltEmailKeys.has(expandKey);
    const showLabel = isOriginalSide
      ? "Replacement Account"
      : "Order Restricted Account";
    const hideLabel = isOriginalSide
      ? "Hide Replacement Account"
      : "Hide Order Restricted Account";
    const toggleClass = isOriginalSide
      ? "tm-alt-email-toggle tm-alt-email-toggle--original"
      : "tm-alt-email-toggle";
    const countClass = isOriginalSide
      ? "tm-alt-email-count tm-alt-email-count--original"
      : "tm-alt-email-count";

    const goToAltEmail = () => {
      const el =
        targetId != null ? document.getElementById(`advice-row-${targetId}`) : null;
      if (el) {
        scrollToAdviceRow(targetId);
      } else {
        copyToClipboard(altEmail);
      }
    };

    return (
      <div className="tm-email-cell">
        <div className="d-flex align-items-center gap-1">
          {linkedReq && (
            <span
              className={
                isOriginalSide
                  ? "tm-account-role-icon tm-account-role-icon--restricted"
                  : "tm-account-role-icon tm-account-role-icon--replacement"
              }
              title={isOriginalSide ? "Order Restricted Account" : "Replacement Account"}
              aria-label={isOriginalSide ? "Order Restricted Account" : "Replacement Account"}
            >
              {isOriginalSide ? "⛔" : "🔁"}
            </span>
          )}
          <span style={{ flex: 1, minWidth: 0 }} className="text-truncate" title={record?.email || ""}>
            {record?.email || "—"}
          </span>
          {record?.email && (
            <button
              className="btn btn-sm btn-outline-secondary"
              style={{ padding: "0.125rem 0.25rem", fontSize: "0.75rem", flexShrink: 0 }}
              onClick={() => copyToClipboard(record.email)}
              title="Copy email"
            >
              📋
            </button>
          )}
        </div>
        {altEmail && (
          <div className="tm-alt-email mt-1">
            <button
              type="button"
              className={toggleClass}
              aria-expanded={isExpanded}
              title={isExpanded ? hideLabel : showLabel}
              onClick={() => {
                setExpandedAltEmailKeys((prev) => {
                  const next = new Set(prev);
                  if (next.has(expandKey)) next.delete(expandKey);
                  else next.add(expandKey);
                  return next;
                });
              }}
            >
              <span className={`tm-alt-email-chevron${isExpanded ? " is-open" : ""}`} aria-hidden>
                ›
              </span>
              <span className={countClass}>1</span>
              <span>{isExpanded ? hideLabel : showLabel}</span>
            </button>
            {isExpanded && (
              <div className={`tm-alt-email-panel${isOriginalSide ? " tm-alt-email-panel--original" : ""}`}>
                <button
                  type="button"
                  className="tm-alt-email-address"
                  title="Go to this account row"
                  onClick={goToAltEmail}
                >
                  {altEmail}
                </button>
                <div className="tm-alt-email-actions">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    style={{ padding: "0.1rem 0.35rem", fontSize: "0.7rem" }}
                    title={
                      isOriginalSide
                        ? "Copy replacement account email"
                        : "Copy order restricted account email"
                    }
                    onClick={() => copyToClipboard(altEmail)}
                  >
                    Copy
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    style={{ padding: "0.1rem 0.35rem", fontSize: "0.7rem" }}
                    title="Jump to linked account row"
                    onClick={goToAltEmail}
                  >
                    Go to row
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const openDetailsRecord = (record) => {
    const tmAccountRequest = tmRequestForRow(record);
    setDetailsRecord && setDetailsRecord({ ...record, tmAccountRequest });
    setShowDetails && setShowDetails(true);
  };

  const renderDetailsCell = (record) => {
    const tmAccountRequest = tmRequestForRow(record);
    const showRequestDetails = !!tmAccountRequest;
    return (
      <div className="d-flex flex-column gap-1 align-items-stretch">
        <button
          className="btn btn-sm btn-outline-secondary"
          onClick={() => openDetailsRecord(record)}
        >
          Details
        </button>
        {showRequestDetails && (
          <button
            className="btn btn-sm btn-outline-primary"
            title={
              isStaff
                ? "View New TM Account request details"
                : "View your submitted request"
            }
            onClick={() => openDetailsRecord(record)}
            style={{ fontSize: "0.7rem", fontWeight: 700, whiteSpace: "nowrap" }}
          >
            Request details
          </button>
        )}
      </div>
    );
  };

  const renderGetNewTmAccountButton = (actionPorts, record) => {
    if (isStaff) return null;
    if (rowHasPendingTmRequest(record, pendingTmRequests)) return null;
    const hasTtOtp = (actionPorts || []).some(portHasAssignedNumber);
    const hasTtProxy = !!(record?.connected_proxy || "").trim();
    if (!hasTtOtp || !hasTtProxy) return null;
    return (
      <button
        type="button"
        className="btn btn-sm btn-outline-primary"
        title="Get a new TM account (requires TT OTP and TT Proxy)"
        onClick={() => setTmAccountModal({ open: true, record })}
        style={{
          fontSize: "0.7rem",
          fontWeight: 700,
          whiteSpace: "nowrap",
        }}
      >
        Get New TM Account
      </button>
    );
  };

  const renderActionButtons = (actionPorts, safeId, record) => {
    const getNewTmAccountButton = renderGetNewTmAccountButton(actionPorts, record);
    if (!actionPorts.length) {
      return getNewTmAccountButton || <span className="text-muted small">—</span>;
    }
    return (
      <div className="d-flex flex-column gap-1">
        {actionPorts.map((port, portIdx) => {
          // Resolve full port so is_sim_converted=2 is visible even if row copy is incomplete
          const fullPort = resolvePortForConversion(port, allPorts);
          const activatePort = portRecordForActivate(port, allPorts);
          const btnState = getButtonState
            ? getButtonState(activatePort)
            : {
                disabled: true,
                className: "btn btn-sm btn-outline-secondary",
                text: "N/A",
                title: "",
              };
          const showConvert =
            !!onRequestSimConversion && canRequestConvertToTtOtp(fullPort);
          // Stay visible until this gateway number itself is converted.
          const showActivate = shouldShowActivate(activatePort) && !btnState.hide;
          return (
            <div
              key={port?.id ? `${safeId}-btn-${port.id}` : `${safeId}-btn-${portIdx}`}
              className="d-flex flex-column gap-1"
            >
              <div className="d-flex gap-1">
                {showActivate ? (
                  <button
                    className={btnState.className}
                    disabled={btnState.disabled}
                    onClick={() => onActivatePort && onActivatePort(port)}
                    title={btnState.title}
                    style={{ minWidth: "80px", fontSize: "0.75rem" }}
                  >
                    {btnState.text}
                  </button>
                ) : null}
                <button
                  className="btn btn-sm btn-danger"
                  onClick={() => onReportProblem && onReportProblem(port)}
                  title="Report a problem with this number"
                  style={{ fontSize: "0.75rem", padding: "0.25rem 0.4rem" }}
                >
                  🚨
                </button>
              </div>
              {showConvert && (
                <button
                  className="btn btn-sm btn-outline-warning"
                  onClick={() =>
                    setConversionModal({ open: true, port: fullPort || port, record })
                  }
                  title="Request conversion of this SIM to a TT OTP number"
                  style={{
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                    borderWidth: 2,
                  }}
                >
                  Convert to TT OTP
                </button>
              )}
            </div>
          );
        })}
        {getNewTmAccountButton}
      </div>
    );
  };

  return (
    <section className="mb-5">
      <style>
        {`
          .resizable-header {
            position: relative;
            user-select: none;
          }
          .resize-handle {
            position: absolute;
            right: 0;
            top: 0;
            bottom: 0;
            width: 8px;
            cursor: col-resize;
            background: transparent;
            z-index: 1;
          }
          .resize-handle:hover {
            background: rgba(0, 123, 255, 0.15);
          }
          .column-search-input {
            width: 100%;
            font-size: 0.875rem;
            padding: 0.25rem 0.5rem;
            border: 1px solid #ced4da;
            border-radius: 0.25rem;
            margin-top: 0.5rem;
          }
          .column-search-input:focus {
            outline: none;
            border-color: #80bdff;
            box-shadow: 0 0 0 0.2rem rgba(0,123,255,.25);
          }
          .resizable-table {
            table-layout: fixed;
            width: 100%;
          }
          .resizable-table th,
          .resizable-table td {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            vertical-align: middle;
          }
          .resizable-table td.proxy-cell {
            white-space: normal;
            word-break: break-all;
            overflow-wrap: anywhere;
            vertical-align: top;
          }
          .resizable-table td.proxy-cell > div {
            max-width: 100%;
          }
          .resizable-table tbody tr.tm-account-requested > td,
          .resizable-table tbody tr.tm-account-requested > th {
            background-color: #cfe2ff !important;
            box-shadow: inset 0 0 0 9999px #cfe2ff !important;
          }
          .resizable-table tbody tr.tm-alt-email-flash > td,
          .resizable-table tbody tr.tm-alt-email-flash > th {
            background-color: #fff3cd !important;
            box-shadow: inset 0 0 0 9999px #fff3cd !important;
          }
          .tm-email-cell {
            min-width: 0;
            white-space: normal;
          }
          .tm-account-role-icon {
            flex-shrink: 0;
            font-size: 0.85rem;
            line-height: 1;
            cursor: default;
          }
          .tm-account-role-icon--restricted {
            filter: saturate(1.1);
          }
          .tm-account-role-icon--replacement {
            filter: saturate(1.05);
          }
          .resizable-table td:has(.tm-email-cell) {
            white-space: normal;
            overflow: visible;
            text-overflow: clip;
            vertical-align: top;
          }
          .tm-alt-email-toggle {
            display: inline-flex;
            align-items: center;
            gap: 0.35rem;
            max-width: 100%;
            padding: 0.15rem 0.4rem;
            border: 1px solid #cfe2ff;
            border-radius: 0.35rem;
            background: #f0f6ff;
            color: #0b5ed7;
            font-size: 0.7rem;
            font-weight: 600;
            line-height: 1.25;
            cursor: pointer;
          }
          .tm-alt-email-toggle:hover {
            background: #e2efff;
            border-color: #9ec5fe;
          }
          .tm-alt-email-toggle--original {
            border-color: #ffe69c;
            background: #fff8e1;
            color: #997404;
          }
          .tm-alt-email-toggle--original:hover {
            background: #fff3cd;
            border-color: #ffda6a;
          }
          .tm-alt-email-chevron {
            display: inline-block;
            width: 0.7rem;
            font-size: 0.85rem;
            line-height: 1;
            transform: rotate(0deg);
            transition: transform 0.15s ease;
          }
          .tm-alt-email-chevron.is-open {
            transform: rotate(90deg);
          }
          .tm-alt-email-count {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-width: 1.1rem;
            height: 1.1rem;
            padding: 0 0.2rem;
            border-radius: 999px;
            background: #0d6efd;
            color: #fff;
            font-size: 0.65rem;
            font-weight: 700;
          }
          .tm-alt-email-count--original {
            background: #cc9a06;
          }
          .tm-alt-email-panel {
            margin-top: 0.35rem;
            padding: 0.4rem 0.5rem;
            border: 1px solid #dee2e6;
            border-left: 3px solid #0d6efd;
            border-radius: 0.35rem;
            background: #f8f9fa;
          }
          .tm-alt-email-panel--original {
            border-left-color: #cc9a06;
          }
          .tm-alt-email-address {
            display: block;
            width: 100%;
            padding: 0;
            border: 0;
            background: transparent;
            color: #0d6efd;
            font-size: 0.75rem;
            font-weight: 600;
            text-align: left;
            word-break: break-all;
            white-space: normal;
            cursor: pointer;
          }
          .tm-alt-email-address:hover {
            text-decoration: underline;
          }
          .tm-alt-email-actions {
            display: flex;
            flex-wrap: wrap;
            gap: 0.35rem;
            margin-top: 0.35rem;
          }
        `}
      </style>
      <div className="table-responsive" ref={tableRef}>
        <table className="table table-striped table-bordered align-middle resizable-table">
          <thead>
            <tr>
              {/* User Code Column - Only show when groupedByUserCode is provided */}
              {groupedByUserCode && (
                <th
                  className="resizable-header"
                  style={{ width: columnWidths.userCode }}
                >
                  <div
                    style={{ cursor: "pointer" }}
                    onClick={() => toggleSort && toggleSort("user_code")}
                  >
                    User Code{" "}
                    {sortField === "user_code" ? (sortDir === "asc" ? "▲" : "▼") : ""}
                  </div>
                  <input
                    type="text"
                    className="column-search-input"
                    placeholder="Search user code..."
                    value={columnFilters.userCode || ""}
                    onChange={(e) =>
                      setColumnFilters &&
                      setColumnFilters({ ...columnFilters, userCode: e.target.value })
                    }
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div
                    className="resize-handle"
                    onMouseDown={(e) => handleResizeStart("userCode", e)}
                  />
                </th>
              )}

              {/* Site Column */}
              <th
                className="resizable-header"
                style={{ width: columnWidths.site }}
              >
                <div
                  style={{ cursor: "pointer" }}
                  onClick={() => toggleSort && toggleSort("site")}
                >
                  Site{" "}
                  {sortField === "site" ? (sortDir === "asc" ? "▲" : "▼") : ""}
                </div>
                <input
                  type="text"
                  className="column-search-input"
                  placeholder="Search site..."
                  value={columnFilters.site || ""}
                  onChange={(e) =>
                    setColumnFilters &&
                    setColumnFilters({ ...columnFilters, site: e.target.value })
                  }
                  onClick={(e) => e.stopPropagation()}
                />
                <div
                  className="resize-handle"
                  onMouseDown={(e) => handleResizeStart("site", e)}
                />
              </th>


              {/* Email Column */}
              <th
                className="resizable-header"
                style={{ width: columnWidths.email }}
              >
                <div
                  style={{ cursor: "pointer" }}
                  onClick={() => toggleSort && toggleSort("email")}
                >
                  Email{" "}
                  {sortField === "email" ? (sortDir === "asc" ? "▲" : "▼") : ""}
                </div>
                <input
                  type="text"
                  className="column-search-input"
                  placeholder="Search email..."
                  value={columnFilters.email || ""}
                  onChange={(e) =>
                    setColumnFilters &&
                    setColumnFilters({ ...columnFilters, email: e.target.value })
                  }
                  onClick={(e) => e.stopPropagation()}
                />
                <div
                  className="resize-handle"
                  onMouseDown={(e) => handleResizeStart("email", e)}
                />
              </th>

              {/* Password Column */}
              <th
                className="resizable-header"
                style={{ width: columnWidths.password }}
              >
                <div>Password</div>
                <input
                  type="text"
                  className="column-search-input"
                  placeholder="Search password..."
                  value={columnFilters.password || ""}
                  onChange={(e) =>
                    setColumnFilters &&
                    setColumnFilters({
                      ...columnFilters,
                      password: e.target.value,
                    })
                  }
                  onClick={(e) => e.stopPropagation()}
                />
                <div
                  className="resize-handle"
                  onMouseDown={(e) => handleResizeStart("password", e)}
                />
              </th>

              {/* Proxy Column -- NEW */}
              <th
                className="resizable-header"
                style={{ width: columnWidths.proxy }}
              >
                <div>Proxy</div>
                <div
                  className="resize-handle"
                  onMouseDown={(e) => handleResizeStart("proxy", e)}
                />
              </th>

              {/* Persona Check */}
              {showPersonaSelfTransfer && (
                <th
                  className="resizable-header text-center"
                  style={{ width: columnWidths.personaCheck }}
                >
                  <div>Persona Check</div>
                  <select
                    className="column-search-input"
                    value={columnFilters.personaCheck || ""}
                    onChange={(e) =>
                      setColumnFilters &&
                      setColumnFilters({
                        ...columnFilters,
                        personaCheck: e.target.value,
                      })
                    }
                    onClick={(e) => e.stopPropagation()}
                  >
                    <option value="">All</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                  <div
                    className="resize-handle"
                    onMouseDown={(e) => handleResizeStart("personaCheck", e)}
                  />
                </th>
              )}

              {/* Self Transfer */}
              {showPersonaSelfTransfer && (
                <th
                  className="resizable-header text-center"
                  style={{ width: columnWidths.selfTransfer }}
                >
                  <div>Self Transfer</div>
                  <select
                    className="column-search-input"
                    value={columnFilters.selfTransfer || ""}
                    onChange={(e) =>
                      setColumnFilters &&
                      setColumnFilters({
                        ...columnFilters,
                        selfTransfer: e.target.value,
                      })
                    }
                    onClick={(e) => e.stopPropagation()}
                  >
                    <option value="">All</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                  <div
                    className="resize-handle"
                    onMouseDown={(e) => handleResizeStart("selfTransfer", e)}
                  />
                </th>
              )}

              {/* Numbers column - RESIZABLE */}
              <th
                className="resizable-header"
                style={{ width: columnWidths.numbers }}
              >
                <div className="d-flex flex-column gap-1">
                  <span>Numbers</span>
                  <input
                    className="form-control form-control-sm"
                    placeholder="Search number..."
                    value={columnFilters.numbers || ""}
                    onChange={(e) =>
                      setColumnFilters &&
                      setColumnFilters({
                        ...columnFilters,
                        numbers: e.target.value,
                      })
                    }
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <div
                  className="resize-handle"
                  onMouseDown={(e) => handleResizeStart("numbers", e)}
                />
              </th>

              {/* Status column - RESIZABLE */}
              <th
                className="resizable-header text-center"
                style={{ width: columnWidths.status }}
              >
                <div>Status</div>
                <div
                  className="resize-handle"
                  onMouseDown={(e) => handleResizeStart("status", e)}
                />
              </th>

              {/* Actions column - RESIZABLE */}
              <th
                className="resizable-header text-center"
                style={{ width: columnWidths.actions }}
              >
                <div>Actions</div>
                <div
                  className="resize-handle"
                  onMouseDown={(e) => handleResizeStart("actions", e)}
                />
              </th>

              {/* Messages Column */}
              <th
                className="resizable-header"
                style={{ width: columnWidths.messages }}
              >
                <div>Recent Messages</div>
                <div
                  className="resize-handle"
                  onMouseDown={(e) => handleResizeStart("messages", e)}
                />
              </th>

              {/* Details Column */}
              <th
                className="resizable-header"
                style={{ width: columnWidths.details }}
              >
                <div>Details</div>
                <div
                  className="resize-handle"
                  onMouseDown={(e) => handleResizeStart("details", e)}
                />
              </th>

              {/* Edit Column */}
              <th
                className="resizable-header"
                style={{ width: columnWidths.edit }}
              >
                <div>Edit</div>
                <div
                  className="resize-handle"
                  onMouseDown={(e) => handleResizeStart("edit", e)}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Backward compatibility: if groupedByUserCode is not provided, use filteredAdvice directly */}
            {!groupedByUserCode && (!Array.isArray(filteredAdvice) || filteredAdvice.length === 0) && (
              <tr>
                <td colSpan={colSpan} className="text-muted">
                  No advice passwords.
                </td>
              </tr>
            )}
            
            {/* Grouped view empty state */}
            {groupedByUserCode && (!groupedByUserCode || groupedByUserCode.length === 0) && (
              <tr>
                <td colSpan={colSpan} className="text-muted">
                  No advice passwords.
                </td>
              </tr>
            )}

            {/* Original behavior: render filteredAdvice directly (for user dashboard) */}
            {!groupedByUserCode && Array.isArray(filteredAdvice) && filteredAdvice.length > 0 && (
              filteredAdvice.map((row, idx) => {
                if (!row || typeof row !== "object") {
                  return (
                    <tr key={`row-empty-${idx}`}>
                      <td colSpan={colSpan} className="text-muted">
                        —
                      </td>
                    </tr>
                  );
                }

                const r = row;
                const safeId = r.id ?? `row-${idx}`;
                const emailVal = (r.email || "").toString();
                const emailKey = emailVal.trim().toLowerCase();
                const linkedPorts = portsByEmail?.get?.(emailKey) || [];
                const isEditing = editingId === r.id;

                // For rows with user_managed_numbers but no direct ports, find the
                // shared ports so messages/status can still be displayed.
                const managedNumsForRow = Array.isArray(r.user_managed_numbers) ? r.user_managed_numbers : [];
                const sharedPorts = linkedPorts.length === 0 && managedNumsForRow.length > 0
                  ? [...new Map(
                      managedNumsForRow
                        .flatMap((n) => portsByNumber.get(String(n).replace(/\D/g, "")) || [])
                        .map((p) => [p.id, p])
                    ).values()]
                  : [];

                // Check if any linked port is from a user-owned gateway (different row color)
                const hasGatewayPort = linkedPorts.some(port => port.gateway?.owner_user_code);
                const isOtherUserSim = currentUserCode && r.user_code && r.user_code !== currentUserCode;
                const isDuplicate = isRowDuplicate(r);
                const isArchived = linkedPorts.length > 0 && linkedPorts.every(p => p?.archived);
                const hasPendingTmRequest = rowHasPendingTmRequest(r, pendingTmRequests);

                return (
                  <tr
                    key={safeId}
                    id={r.id != null ? `advice-row-${r.id}` : undefined}
                    className={rowClassName(r, hasPendingTmRequest)}
                    style={
                      hasPendingTmRequest
                        ? TM_REQUEST_ROW_STYLE
                        : isDuplicate
                        ? { backgroundColor: "#ffe0e0", borderLeft: "4px solid #dc3545" }
                        : isArchived
                        ? { backgroundColor: "#f8d7da", borderLeft: "4px solid #dc3545", opacity: 0.75 }
                        : {
                            backgroundColor: isOtherUserSim ? '#ffe6e6' : hasGatewayPort ? '#fff3cd' : undefined,
                            borderLeft: isOtherUserSim ? '8px solid #dc3545' : hasGatewayPort ? '8px solid #ffc107' : undefined,
                            borderRight: isOtherUserSim ? '4px solid #dc3545' : hasGatewayPort ? '4px solid #ffc107' : undefined,
                            fontWeight: (isOtherUserSim || hasGatewayPort) ? '600' : undefined,
                            boxShadow: isOtherUserSim ? 'inset 0 0 0 1px rgba(220, 53, 69, 0.4)' : hasGatewayPort ? 'inset 0 0 0 1px rgba(255, 193, 7, 0.4)' : undefined,
                          }
                    }
                    title={
                      hasPendingTmRequest
                        ? "New TM account requested"
                        : isDuplicate
                        ? "⚠ Duplicate email or phone number detected"
                        : isArchived
                        ? "⚠ Number no longer in use — replacement needed"
                        : undefined
                    }
                  >
                    {/* Site Column */}
                    <td>
                      {isEditing ? (
                        <input
                          className="form-control form-control-sm"
                          value={editDraft?.site ?? ""}
                          onChange={(e) =>
                            setEditDraft &&
                            setEditDraft({ ...editDraft, site: e.target.value })
                          }
                          style={{ maxWidth: 180 }}
                        />
                      ) : (
                        r.site || "—"
                      )}
                    </td>
                    <td>
                      {isEditing ? (
                        <input
                          className="form-control form-control-sm"
                          value={editDraft?.email ?? ""}
                          onChange={(e) =>
                            setEditDraft &&
                            setEditDraft({
                              ...editDraft,
                              email: e.target.value,
                            })
                          }
                          style={{ maxWidth: 280 }}
                        />
                      ) : (
                        renderEmailDisplay(r)
                      )}
                    </td>
                    <td>
                      {isEditing ? (
                        <Secret
                          value={editDraft?.password ?? ""}
                          onChange={(v) =>
                            setEditDraft &&
                            setEditDraft({ ...editDraft, password: v })
                          }
                          editable
                        />
                      ) : (
                        <Secret value={r.password || ""} />
                      )}
                    </td>

                    {/* Proxy display */}
                    <td className="proxy-cell">
                      <ProxyDisplay
                        connectedProxy={r.connected_proxy}
                        proxy={r.proxy}
                        mlProxy={r.ml_proxy}
                        mlFolderName={r.ml_folder_name}
                        mlProfileName={r.ml_profile_name}
                        simplifyOnMatch={simplifyMatchingProxy}
                      />
                    </td>

                    {showPersonaSelfTransfer && renderPersonaCheckCell(r)}
                    {showPersonaSelfTransfer && renderSelfTransferCell(r)}

                    {/* Numbers (phone numbers only) */}
                    <td>{renderNumbersCell(r, linkedPorts, safeId)}</td>

                    {/* Status badges */}
                    <td className="text-center">
                      {linkedPorts.length === 0 && sharedPorts.length === 0 ? (
                        <span className="text-muted small">—</span>
                      ) : (
                        <div className="d-flex flex-column gap-1">
                          {(linkedPorts.length > 0 ? linkedPorts : sharedPorts).map((port, portIdx) => (
                            <div key={port?.id ? `${safeId}-status-${port.id}` : `${safeId}-status-${portIdx}`}>
                              {renderPortStatus(port, safeId, portIdx)}
                            </div>
                          ))}
                        </div>
                      )}
                    </td>

                    {/* Action buttons — email-linked ports, or managed numbers that match a port */}
                    <td className="text-center">
                      {renderActionButtons(
                        linkedPorts.length > 0 ? linkedPorts : sharedPorts,
                        safeId,
                        r
                      )}
                    </td>

                    {/* Messages Column */}
                    <td>
                      {linkedPorts.length === 0 && sharedPorts.length === 0 ? (
                        <span className="text-muted">—</span>
                      ) : (
                        <div className="d-flex flex-column gap-2">
                          {(linkedPorts.length > 0 ? linkedPorts : sharedPorts).map((port, portIdx) => {
                            const messages = portMessages?.[port?.id] || [];
                            return (
                              <div key={port?.id ? `${safeId}-msg-${port.id}` : `${safeId}-msg-${portIdx}`}>
                                <MessageCarousel
                                  messages={messages}
                                  portNumber={port?.assigned_number || "—"}
                                />
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </td>

                    {/* Details */}
                    <td>{renderDetailsCell(r)}</td>

                    {/* Edit */}
                    <td>{renderEditCell(r, linkedPorts, isEditing)}</td>
                  </tr>
                );
              })
            )}

            {/* New behavior: grouped by user code (for staff dashboard) */}
            {groupedByUserCode && groupedByUserCode.length > 0 && (
              groupedByUserCode.map((group, groupIdx) => {
                return (
                  <React.Fragment key={`group-${group.userCode}`}>
                    {group.rows.map((row, rowIdx) => {
                      if (!row || typeof row !== "object") {
                        return (
                          <tr key={`row-empty-${groupIdx}-${rowIdx}`}>
                            <td colSpan={colSpan} className="text-muted">
                              —
                            </td>
                          </tr>
                        );
                      }

                      const r = row;
                      const safeId = r.id ?? `row-${groupIdx}-${rowIdx}`;
                      const emailVal = (r.email || "").toString();
                      const emailKey = emailVal.trim().toLowerCase();
                      const linkedPorts = portsByEmail?.get?.(emailKey) || [];
                      const isEditing = editingId === r.id;
                      const isFirstInGroup = rowIdx === 0;

                      // For rows with user_managed_numbers but no direct ports, find the
                      // shared ports so messages/status can still be displayed.
                      const managedNumsForRow = Array.isArray(r.user_managed_numbers) ? r.user_managed_numbers : [];
                      const sharedPorts = linkedPorts.length === 0 && managedNumsForRow.length > 0
                        ? [...new Map(
                            managedNumsForRow
                              .flatMap((n) => portsByNumber.get(String(n).replace(/\D/g, "")) || [])
                              .map((p) => [p.id, p])
                          ).values()]
                        : [];

                      // Check if any linked port is from a user-owned gateway (different row color)
                      const hasGatewayPort = linkedPorts.some(port => port.gateway?.owner_user_code);
                      const isOtherUserSim = currentUserCode && r.user_code && r.user_code !== currentUserCode;
                      const isDuplicate = isRowDuplicate(r);
                      const isArchived = linkedPorts.length > 0 && linkedPorts.every(p => p?.archived);
                      const hasPendingTmRequest = rowHasPendingTmRequest(r, pendingTmRequests);

                      return (
                        <tr
                          key={safeId}
                          id={r.id != null ? `advice-row-${r.id}` : undefined}
                          className={rowClassName(r, hasPendingTmRequest)}
                          style={
                            hasPendingTmRequest
                              ? TM_REQUEST_ROW_STYLE
                              : isDuplicate
                              ? { backgroundColor: "#ffe0e0", borderLeft: "4px solid #dc3545" }
                              : isArchived
                              ? { backgroundColor: "#f8d7da", borderLeft: "4px solid #dc3545", opacity: 0.75 }
                              : {
                                  backgroundColor: isOtherUserSim
                                    ? "#ffe6e6"
                                    : hasGatewayPort
                                    ? "#fff3cd"
                                    : isFirstInGroup
                                    ? "rgba(0, 123, 255, 0.05)"
                                    : undefined,
                                  borderLeft: isOtherUserSim
                                    ? "8px solid #dc3545"
                                    : hasGatewayPort
                                    ? "8px solid #ffc107"
                                    : isFirstInGroup
                                    ? "3px solid #007bff"
                                    : undefined,
                                  borderRight: isOtherUserSim ? "4px solid #dc3545" : hasGatewayPort ? "4px solid #ffc107" : undefined,
                                  fontWeight: isOtherUserSim || hasGatewayPort ? "600" : undefined,
                                  boxShadow: isOtherUserSim ? "inset 0 0 0 1px rgba(220, 53, 69, 0.4)" : hasGatewayPort ? "inset 0 0 0 1px rgba(255, 193, 7, 0.4)" : undefined,
                                }
                          }
                          title={
                            hasPendingTmRequest
                              ? "New TM account requested"
                              : isDuplicate
                              ? "⚠ Duplicate email or phone number detected"
                              : isArchived
                              ? "⚠ Number no longer in use — replacement needed"
                              : undefined
                          }
                        >
                          {/* User Code Column */}
                          <td
                            style={{
                              fontWeight: isFirstInGroup ? "600" : "normal",
                              borderLeft: isFirstInGroup
                                ? "3px solid #007bff"
                                : undefined,
                            }}
                          >
                            {isFirstInGroup ? (
                              <span className="badge bg-primary">{r.user_code || "UNKNOWN"}</span>
                            ) : (
                              <span className="text-muted" style={{ fontSize: "0.85em" }}>
                                {r.user_code || "—"}
                              </span>
                            )}
                          </td>

                          {/* Site Column */}
                          <td>
                            {isEditing ? (
                              <SearchableSelect
                                value={editDraft?.site ?? ""}
                                onChange={(value) =>
                                  setEditDraft &&
                                  setEditDraft({ ...editDraft, site: value })
                                }
                                options={sites.map(site => ({ value: site, label: site }))}
                                placeholder="Site"
                                allowCustom={isStaff}
                                style={{ maxWidth: 180 }}
                              />
                            ) : (
                              r.site || "—"
                            )}
                          </td>


                          {/* Email Column */}
                          <td>
                            {isEditing ? (
                              <input
                                className="form-control form-control-sm"
                                value={editDraft?.email ?? ""}
                                onChange={(e) =>
                                  setEditDraft &&
                                  setEditDraft({
                                    ...editDraft,
                                    email: e.target.value,
                                  })
                                }
                                style={{ maxWidth: 280 }}
                              />
                            ) : (
                              renderEmailDisplay(r)
                            )}
                          </td>

                          {/* Password Column */}
                          <td>
                            {isEditing ? (
                              <Secret
                                value={editDraft?.password ?? ""}
                                onChange={(v) =>
                                  setEditDraft &&
                                  setEditDraft({ ...editDraft, password: v })
                                }
                                editable
                              />
                            ) : (
                              <Secret value={r.password || ""} />
                            )}
                          </td>

                          {/* Proxy display */}
                          <td className="proxy-cell">
                            <ProxyDisplay
                              connectedProxy={r.connected_proxy}
                              proxy={r.proxy}
                              mlProxy={r.ml_proxy}
                              mlFolderName={r.ml_folder_name}
                              mlProfileName={r.ml_profile_name}
                              simplifyOnMatch={simplifyMatchingProxy}
                            />
                          </td>

                          {showPersonaSelfTransfer && renderPersonaCheckCell(r)}
                          {showPersonaSelfTransfer && renderSelfTransferCell(r)}

                          {/* Numbers (phone numbers only) */}
                          <td>{renderNumbersCell(r, linkedPorts, safeId)}</td>

                          {/* Status badges */}
                          <td className="text-center">
                            {linkedPorts.length === 0 && sharedPorts.length === 0 ? (
                              <span className="text-muted small">—</span>
                            ) : (
                              <div className="d-flex flex-column gap-1">
                                {(linkedPorts.length > 0 ? linkedPorts : sharedPorts).map((port, portIdx) => (
                                  <div key={port?.id ? `${safeId}-status-${port.id}` : `${safeId}-status-${portIdx}`}>
                                    {renderPortStatus(port, safeId, portIdx)}
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>

                          {/* Action buttons — email-linked ports, or managed numbers that match a port */}
                          <td className="text-center">
                            {renderActionButtons(
                              linkedPorts.length > 0 ? linkedPorts : sharedPorts,
                              safeId,
                              r
                            )}
                          </td>

                          {/* Messages Column */}
                          <td>
                            {linkedPorts.length === 0 && sharedPorts.length === 0 ? (
                              <span className="text-muted">—</span>
                            ) : (
                              <div className="d-flex flex-column gap-2">
                                {(linkedPorts.length > 0 ? linkedPorts : sharedPorts).map((port, portIdx) => {
                                  const messages = portMessages?.[port?.id] || [];
                                  return (
                                    <div key={port?.id ? `${safeId}-msg-${port.id}` : `${safeId}-msg-${portIdx}`}>
                                      <MessageCarousel
                                        messages={messages}
                                        portNumber={port?.assigned_number || "—"}
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </td>

                          {/* Details */}
                          <td>{renderDetailsCell(r)}</td>

                          {/* Edit */}
                          <td>{renderEditCell(r, linkedPorts, isEditing)}</td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {showPersonaSelfTransfer && (
        <PersonaCheckModal
          open={personaModal.open}
          mode={personaModal.mode}
          record={personaModal.record}
        sessionStampedAs={getSessionStampedAs()}
        sessionWho={getSessionStampWho()}
        onClose={() => setPersonaModal({ open: false, mode: "edit", record: null })}
        onSubmit={handlePersonaModalSubmit}
      />
      )}

      {showPersonaSelfTransfer && (
        <SelfTransferConfirmModal
          open={selfTransferModal.open}
          email={selfTransferModal.record?.email}
          onClose={() => setSelfTransferModal({ open: false, record: null })}
          onConfirm={handleSelfTransferConfirm}
        />
      )}

      <ConvertToTtOtpModal
        open={conversionModal.open}
        port={conversionModal.port}
        record={conversionModal.record}
        isStaff={isStaff}
        onClose={() => setConversionModal({ open: false, port: null, record: null })}
        onConfirm={handleConversionConfirm}
      />
      <GetNewTmAccountModal
        open={tmAccountModal.open}
        record={tmAccountModal.record}
        onClose={() => setTmAccountModal({ open: false, record: null })}
        onSubmitted={markTmAccountRequested}
      />
    </section>
  );
}
